import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { withRouter, useParams, useHistory } from "react-router-dom";

import {
  ZERO,
  ROUTES,
  messages,
  LOGIN_FORM,
  EMPTY_STRING,
  localStorageKeys,
  eventBookingParams,
  eventBookingStatus,
  eventBookingConstants,
  defaultEventSetupObject,
} from "../../../../constants";
import {
  deepClone,
  customToast,
  scrollToTop,
  filterByStatus,
  generateEvents,
  formatDraftEvents,
  ClearEventStorage,
  hasValidOrderItems,
  areAllEventsIsDraft,
  isMatchingDraftEventGroup,
  checkShutterBSelectionValid,
} from "../../../../shared";
import {
  NavigateToPath,
  setItemToStorage,
  getItemFromStorage,
} from "../../../../system";
import { eventBookingActions } from "../../../../redux/actions";

import Modal from "../../../../container/Views/Login/Modal";
import EventSelection from "../../ShutterBSelection/EventSelection";
import BackButton from "../../../../shared/components/IconButton/BackButton";
import ShutterBSelectionPage from "../../ShutterBSelection/ShutterBSelectionPage";
import AnimationWrapper from "../../../../shared/components/Animation/AnimationWrapper";

const { TABS_ID } = eventBookingConstants;

const ShutterBSelectionForm = (props) => {
  const history = useHistory();
  const { t } = useTranslation("common");
  const { isBookingStatus, eventGroupId } = useParams();

  const isDraftEvent = isBookingStatus === eventBookingParams.DRAFT_EVENT;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHideContinueButton, setIsHideContinueButton] = useState(false);

  const currentIndex = Array.isArray(props.formState)
    ? props.formState.findIndex((e) => e.id === props.selectedEventId)
    : -1;
  const isLastEvent = currentIndex === props.formState.length - 1;

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line
  }, [currentIndex]);

  useEffect(() => {
    if (props.activeTab === TABS_ID.SELECT_SHUTTERB) {
      if (isDraftEvent && eventGroupId) {
        if (
          !isMatchingDraftEventGroup(
            eventGroupId,
            props.draftEvents?.eventGroupId
          )
        ) {
          const tempToken = getItemFromStorage(process.env.REACT_APP_PAYLOAD);
          localStorage.clear();
          if (tempToken) {
            setItemToStorage(process.env.REACT_APP_PAYLOAD, tempToken);
          }
          props.getDraftEvents(eventGroupId, onhandleDraftEventResponse);
        }
        props.setIsDraftEvent(isDraftEvent);
      } else {
        const existingData = localStorage.getItem(
          localStorageKeys.DATA_TO_SEND
        );
        const parsedData = JSON.parse(existingData);

        if (Array.isArray(parsedData?.Event)) {
          const dataToSend = filterByStatus(parsedData.Event, [
            eventBookingStatus.CANCEL,
          ]).map((item) => ({
            eventId: item.id,
            eventDate: item?.eventDate ?? null,
            sbStartTime: item?.sbStartTime ?? null,
            userLatitude: item?.eventLocation?.latitude ?? null,
            userLongitude: item?.eventLocation?.longitude ?? null,
            duration: item?.duration ? Number(item.duration) : null,
            postalCode: item?.eventLocation?.postalCode ?? null,
            SourceTimeZone:
              Intl?.DateTimeFormat()?.resolvedOptions().timeZone || "UTC",
            status: item?.status ?? eventBookingStatus.CREATED,
          }));

          props.getAvailableShutterBs(dataToSend);
        } else {
          console.warn(
            messages.PARSE_DATA_EVENT_IS_NOT_AN_ARRAY,
            parsedData?.Event
          );
        }
      }

      if (props.formState?.length > 0) {
        const firstEventId = props.formState[0].id;
        props.setEventName(props.formState[0].eventName);
        props.setSelectedEventId(firstEventId);
      } else {
        props.setSelectedEventId(null);
        props.setEventName(EMPTY_STRING);
      }
    }
    // eslint-disable-next-line
  }, [props.activeTab]);

  const onhandleDraftEventResponse = (response) => {
    if (response) {
      const updatedEvents = formatDraftEvents(response.events);
      props.setFormState(updatedEvents);

      if (updatedEvents.length > 0) {
        const firstEventId = updatedEvents[0].id;
        props.setEventName(updatedEvents[0].eventName);
        props.setSelectedEventId(firstEventId);
      } else {
        props.setSelectedEventId(null);
        props.setEventName(EMPTY_STRING);
      }
    } else {
      ClearEventStorage();
      props.setFormState([deepClone(defaultEventSetupObject)]);
      props.setActiveTab(eventBookingConstants.TABS_ID.EVENT_SETUP);
      NavigateToPath(history, ROUTES.EVENT_BOOKING);
    }
    props.setDraftEvents(response);
  };

  const handlePrevious = () => {
    if (props.isProfileViewShow) {
      props.setIsProfileViewShow(false);
    } else {
      props.setEventName(EMPTY_STRING);
      props.onPrevious();
    }
  };

  const handleEventSlider = (event) => {
    if (!hasValidOrderItems(props.formState, currentIndex, event.id)) {
      customToast(messages.PLEASE_SELECT_SHUTTERB, toast.TYPE.ERROR);
      scrollToTop();
      return;
    }
    props.setSelectedEventId(event.id);
    props.setEventName(event.eventName);
  };

  const handleNextEvent = (formState) => {
    // Validate if the shutterB selected for the current event is valid, saved as draft, or status notifying the admin
    if (!hasValidOrderItems(formState, currentIndex)) {
      customToast(messages.PLEASE_SELECT_SHUTTERB, toast.TYPE.ERROR);
      scrollToTop();
      return;
    }

    if (isLastEvent) {
      // Move to next tab
      const allEventsSelected = checkShutterBSelectionValid(
        formState,
        props.availableShutterBs,
        props.isDraftEvent,
        props.isEditEvent,
        eventBookingStatus
      );

      if (allEventsSelected !== null) {
        customToast(messages.PLEASE_SELECT_SHUTTERB, toast.TYPE.ERROR);
        props.setSelectedEventId(allEventsSelected);
        const invalidEvent = formState.find(
          (event) => event.id === allEventsSelected
        );
        if (invalidEvent) props.setEventName(invalidEvent.eventName);
        scrollToTop();
        return;
      }

      // Check if all events are drafts
      const allDraftEvent = areAllEventsIsDraft(formState);

      if (allDraftEvent) {
        const draftEvent = generateEvents(
          props.userId,
          null,
          false,
          false,
          props.affiliateCode
        );

        const firstEvent =
          Array.isArray(draftEvent) && draftEvent.length > 0
            ? draftEvent[0]
            : null;

        const dataToSend = {
          order: {
            serviceFee: ZERO,
            tax: ZERO,
            discount: ZERO,
            totalAmount: ZERO,
            orderDiscountDetail: null,
            isNewBooking: eventBookingConstants.IS_NEW_BOOKING.NEW_EVENT,
            userId: props.userId,
            reels: [],
            events: draftEvent || [],
            eventGroupId: firstEvent?.eventGroupId || EMPTY_STRING,
            eventGroupName:
              firstEvent?.eventGroupName ||
              firstEvent?.eventName ||
              EMPTY_STRING,
          },
        };

        if (props.isAuthenticated) {
          props.checkOut(dataToSend);
        } else {
          setIsModalOpen(true);
        }

        return;
      }

      props.onNext();
    } else {
      const nextEvent = formState[currentIndex + 1];
      props.setSelectedEventId(nextEvent.id);
      props.setEventName(nextEvent.eventName);
    }
  };

  const isSaveAsDraft = () => {
    return (
      props.formState.length && props.formState.some((f) => f.isSaveAsDraft)
    );
  };

  return (
    <>
      <div className="shutterb-selection-container">
        {!props.isProfileViewShow && (
          <>
            <EventSelection
              formState={props.formState ?? []}
              handleEventClick={handleEventSlider}
            />
            <div className="row">
              <div className="col-12">
                <hr className="mt-0" />
              </div>
            </div>
          </>
        )}

        <AnimationWrapper animationKey={props.selectedEventId}>
          <ShutterBSelectionPage
            formState={props.formState}
            activeTab={props.activeTab}
            onNext={props.onNext}
            handlePrevious={handlePrevious}
            handleNextEvent={handleNextEvent}
            resetAvFormKey={props.resetAvFormKey}
            setIsHideContinueButton={setIsHideContinueButton}
          />
        </AnimationWrapper>

        {!props.isProfileViewShow &&
          (!(isHideContinueButton && isLastEvent) || isSaveAsDraft()) && (
            <div className="d-flex justify-content-center sticky-button-row sticky-desktop px-3 mt-5">
              <button
                type="button"
                className="button-common button-fill"
                onClick={() => handleNextEvent(props.formState)}
              >
                {isLastEvent ? t("continue") : t("next")}
              </button>
            </div>
          )}

        {(!props.isDraftEvent || props.isProfileViewShow) && (
          <BackButton
            className="event-back-button"
            onClick={handlePrevious}
            text={`${
              props.isProfileViewShow
                ? t("shutterb_results")
                : props.previousActiveTab
            }`}
          />
        )}
      </div>

      {!props.isAuthenticated && isModalOpen && (
        <Modal
          showBackButton={false}
          formType={LOGIN_FORM}
          setIsModalOpen={setIsModalOpen}
          isAllowModal
        />
      )}
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  isEditEvent: state.eventBooking.isEditEvent,
  draftEvents: state.eventBooking.draftEvents,
  isDraftEvent: state.eventBooking.isDraftEvent,
  availableShutterBs: state.eventBooking.availableShutterBs,
  selectedEventId: state.eventBooking.selectedEventId,
  isProfileViewShow: state.eventBooking.isProfileViewShow,
  previousActiveTab: state.eventBooking.previousActiveTab,
  userId: state.authentication.userId,
  isAuthenticated: state.authentication.isAuthenticated,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  checkOut: (data) => dispatch(eventBookingActions.checkOut(data)),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  setEventName: (data) => dispatch(eventBookingActions.setEventName(data)),
  setDraftEvents: (data) => dispatch(eventBookingActions.setDraftEvents(data)),
  setSelectedEventId: (data) =>
    dispatch(eventBookingActions.setSelectedEventId(data)),
  setIsDraftEvent: (data) =>
    dispatch(eventBookingActions.setIsDraftEvent(data)),
  setIsProfileViewShow: (data) =>
    dispatch(eventBookingActions.setIsProfileViewShow(data)),
  getAvailableShutterBs: (data) =>
    dispatch(eventBookingActions.getAvailableShutterBs(data)),
  getDraftEvents: (eventGroupId, onResponseCallback) =>
    dispatch(
      eventBookingActions.getDraftEvents(eventGroupId, onResponseCallback)
    ),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ShutterBSelectionForm));
